import type {
  WidgetCoreDefinition,
  TeXMLContext,
  TeXMLElement,
  ValidationResult,
  TelnyxEvent,
  CallbackResult,
} from './types';
import { buildCallbackUrl } from './types';

export type Condition = {
  id: string;
  variable: string;
  operator: string;
  value: string;
  label: string;
};

export type ConditionalConfig = {
  conditions: Condition[];
  defaultBranch: boolean;
};

function generateTeXML(config: ConditionalConfig, context: TeXMLContext): TeXMLElement[] {
  const callbackUrl = buildCallbackUrl(context);

  return [
    {
      tag: 'Redirect',
      attributes: {
        method: 'POST',
      },
      content: callbackUrl,
    },
  ];
}

export function evaluateCondition(condition: Condition, variables: Record<string, string>): boolean {
  const actualValue = variables[condition.variable] || '';
  const expectedValue = condition.value;

  switch (condition.operator) {
    case 'equals':
      return actualValue === expectedValue;
    case 'not_equals':
      return actualValue !== expectedValue;
    case 'contains':
      return actualValue.includes(expectedValue);
    case 'not_contains':
      return !actualValue.includes(expectedValue);
    case 'starts_with':
      return actualValue.startsWith(expectedValue);
    case 'ends_with':
      return actualValue.endsWith(expectedValue);
    case 'greater_than':
      return parseFloat(actualValue) > parseFloat(expectedValue);
    case 'less_than':
      return parseFloat(actualValue) < parseFloat(expectedValue);
    case 'is_empty':
      return actualValue === '';
    case 'is_not_empty':
      return actualValue !== '';
    default:
      return false;
  }
}

function callbackHandler(
  _event: TelnyxEvent,
  config: ConditionalConfig,
  variables: Record<string, string>,
): CallbackResult {
  for (const condition of config.conditions) {
    if (evaluateCondition(condition, variables)) {
      return { nextHandleId: condition.id };
    }
  }

  if (config.defaultBranch) {
    return { nextHandleId: 'default' };
  }

  return {};
}

function resolveEdge(config: ConditionalConfig, variables: Record<string, string>): string | undefined {
  for (const condition of config.conditions) {
    if (evaluateCondition(condition, variables)) return condition.id;
  }
  if (config.defaultBranch) return 'default';
  return undefined;
}

function validateConfig(config: ConditionalConfig): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (config.conditions.length === 0) {
    errors.push('At least one condition is required');
  }

  const usedIds = new Set<string>();
  config.conditions.forEach((condition, index) => {
    if (usedIds.has(condition.id)) {
      errors.push(`Duplicate condition ID "${condition.id}" at index ${index + 1}`);
    }
    usedIds.add(condition.id);

    if (!condition.variable.trim()) {
      errors.push(`Condition ${index + 1}: Variable name is required`);
    }

    if (!condition.label.trim()) {
      warnings.push(`Condition ${index + 1}: Label is empty`);
    }
  });

  return { valid: errors.length === 0, errors, warnings };
}

export const conditionalWidgetCore: WidgetCoreDefinition<ConditionalConfig> = {
  id: 'conditional',
  version: 1,
  name: 'Conditional',
  description: 'Branch the flow based on variable conditions',
  category: 'control',
  tier: 'standard',

  defaultConfig: {
    conditions: [],
    defaultBranch: true,
  },

  texml: {
    generate: generateTeXML,
    requiresCallback: true,
    resolveEdge,
    callbackHandler,
  },

  validation: {
    validateConfig,
  },
};
